import { LearningLayout } from "@jambushrushti/components/learning/LearningLayout";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Badge } from "@jambushrushti/components/ui/badge";
import { Textarea } from "@jambushrushti/components/ui/textarea";
import { Switch } from "@jambushrushti/components/ui/switch";
import { Label } from "@jambushrushti/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@jambushrushti/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@jambushrushti/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@jambushrushti/components/ui/table";
import {
  Plus,
  Pencil,
  Trash2,
  FileText,
  Search,
  Clock,
  CheckCircle,
} from "lucide-react";
import { useState } from "react";
import {
  useAssignments,
  useSaveAssignment,
  useDeleteAssignment,
} from "@jambushrushti/hooks/useLMSAssignments";
import { useAdminCourses } from "@jambushrushti/hooks/useLMS";
import { format } from "date-fns";

const AssignmentsManage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCourseId, setSelectedCourseId] = useState<string>("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<any>(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    instructions: "",
    course_id: "",
    due_date: "",
    max_score: 100,
    is_published: false,
  });

  const { data: assignments = [], isLoading } = useAssignments(
    selectedCourseId !== "all" ? selectedCourseId : undefined
  );
  const { data: courses = [] } = useAdminCourses();
  const saveAssignment = useSaveAssignment();
  const deleteAssignment = useDeleteAssignment();

  const filteredAssignments = assignments.filter(a =>
    a.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      instructions: "",
      course_id: "",
      due_date: "",
      max_score: 100,
      is_published: false,
    });
    setEditingAssignment(null);
  };

  const handleEdit = (assignment: any) => {
    setEditingAssignment(assignment);
    setFormData({
      title: assignment.title,
      description: assignment.description || "",
      instructions: assignment.instructions || "",
      course_id: assignment.course_id || "",
      due_date: assignment.due_date ? assignment.due_date.split("T")[0] : "",
      max_score: assignment.max_score || 100,
      is_published: assignment.is_published,
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    await saveAssignment.mutateAsync({
      ...(editingAssignment ? { id: editingAssignment.id } : {}),
      ...formData,
      course_id: formData.course_id || null,
      due_date: formData.due_date
        ? new Date(formData.due_date).toISOString()
        : null,
    });
    setIsDialogOpen(false);
    resetForm();
  };

  const handleDelete = async (id: string) => {
    if (confirm("Are you sure you want to delete this assignment?")) {
      await deleteAssignment.mutateAsync(id);
    }
  };

  const getCourseName = (courseId: string | null) => {
    if (!courseId) {
      return "-";
    }
    const course = courses.find(c => c.id === courseId);
    return course?.title || "-";
  };

  return (
    <LearningLayout title="Assignments">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <h1 className="font-heading text-2xl font-bold">
              Manage Assignments
            </h1>
            <p className="text-muted-foreground">
              Create and manage assignments for learners
            </p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setIsDialogOpen(true);
            }}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Assignment
          </Button>
        </div>

        {/* Stats */}
        <div className="grid gap-4 sm:grid-cols-4">
          <Card className="border-border">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="rounded-lg bg-primary/10 p-2 text-primary">
                <FileText className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">{assignments.length}</p>
                <p className="text-xs text-muted-foreground">
                  Total Assignments
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="rounded-lg bg-green-500/10 p-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {assignments.filter(a => a.is_published).length}
                </p>
                <p className="text-xs text-muted-foreground">Published</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="bg-orange/10 text-orange rounded-lg p-2">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {
                    assignments.filter(
                      a => a.due_date && new Date(a.due_date) > new Date()
                    ).length
                  }
                </p>
                <p className="text-xs text-muted-foreground">Upcoming</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="rounded-lg bg-blue-500/10 p-2 text-blue-600">
                <FileText className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {assignments.filter(a => !a.is_published).length}
                </p>
                <p className="text-xs text-muted-foreground">Draft</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search assignments..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={selectedCourseId} onValueChange={setSelectedCourseId}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filter by course" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Courses</SelectItem>
              {courses.map(course => (
                <SelectItem key={course.id} value={course.id}>
                  {course.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card className="border-border">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Assignment</TableHead>
                  <TableHead>Course</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Max Score</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-8 text-center text-muted-foreground"
                    >
                      Loading assignments...
                    </TableCell>
                  </TableRow>
                ) : filteredAssignments.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-8 text-center text-muted-foreground"
                    >
                      No assignments found. Create your first assignment!
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAssignments.map(assignment => (
                    <TableRow key={assignment.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{assignment.title}</p>
                          <p className="line-clamp-1 text-sm text-muted-foreground">
                            {assignment.description}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getCourseName(assignment.course_id)}
                      </TableCell>
                      <TableCell>
                        {assignment.due_date
                          ? format(
                              new Date(assignment.due_date),
                              "MMM dd, yyyy"
                            )
                          : "-"}
                      </TableCell>
                      <TableCell>{assignment.max_score}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            assignment.is_published ? "default" : "secondary"
                          }
                        >
                          {assignment.is_published ? "Published" : "Draft"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(assignment)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => handleDelete(assignment.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingAssignment ? "Edit Assignment" : "Add New Assignment"}
              </DialogTitle>
              <DialogDescription>
                Create an assignment for learners to complete.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={e =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  placeholder="Enter assignment title"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={e =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Brief description"
                  rows={2}
                />
              </div>
              <div>
                <Label>Instructions</Label>
                <Textarea
                  value={formData.instructions}
                  onChange={e =>
                    setFormData({ ...formData, instructions: e.target.value })
                  }
                  placeholder="Detailed instructions for learners"
                  rows={3}
                />
              </div>
              <div>
                <Label>Course</Label>
                <Select
                  value={formData.course_id}
                  onValueChange={value =>
                    setFormData({ ...formData, course_id: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select course" />
                  </SelectTrigger>
                  <SelectContent>
                    {courses.map(course => (
                      <SelectItem key={course.id} value={course.id}>
                        {course.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={e =>
                      setFormData({ ...formData, due_date: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Max Score</Label>
                  <Input
                    type="number"
                    value={formData.max_score}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        max_score: parseInt(e.target.value) || 100,
                      })
                    }
                  />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Label>Publish Assignment</Label>
                <Switch
                  checked={formData.is_published}
                  onCheckedChange={checked =>
                    setFormData({ ...formData, is_published: checked })
                  }
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!formData.title || saveAssignment.isPending}
              >
                {saveAssignment.isPending
                  ? "Saving..."
                  : editingAssignment
                    ? "Update"
                    : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </LearningLayout>
  );
};

export default AssignmentsManage;
